import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface BylawsDraft {
  // Basics
  orgName: string | null;
  schoolName: string | null;
  state: string | null;

  // Membership
  membersIncludeParentsGuardians: boolean;
  membersIncludePrincipal: boolean;
  membersIncludeStaff: boolean;
  duesRequired: boolean;
  duesAmount: number | null;

  // Officers present
  officerPresident: boolean;
  officerVicePresident: boolean;
  officerTreasurer: boolean;
  officerViceTreasurer: boolean;
  officerSecretary: boolean;
  officerMemberAtLarge: boolean;
  officerCommunications: boolean;
  officerVolunteerCoordinator: boolean;

  // Officers summary
  officers: string;

  // Terms
  termLength: number | null;
  termLimit: number | null;
  electionMonth: string | null;

  // Meetings / quorum / notices
  meetingFrequency: string | null;
  quorumExecMode: string | null;
  quorumExecNumber: number | null;
  noticeDays: number | null;

  // Finances
  fiscalYearStartMonth: string | null;
  fiscalYearStartDay: number | null;
  fiscalYearEndMonth: string | null;
  fiscalYearEndDay: number | null;
  routineSpendingLimit: number | null;
  nonBudgetedBoardLimit: number | null;
  auditMethod: string | null;

  // Compliance
  conflictAppliesTo: string | null;
  conflictRecordkeeping: string | null;

  // Amendments
  amendmentVoteThreshold: string | null;
  amendmentNoticeDays: number | null;
}

@Injectable({ providedIn: 'root' })
export class BylawsWizardService {
  private readonly storageKey = 'bylawsDraft';
  private readonly subject = new BehaviorSubject<BylawsDraft | null>(
    this.load(),
  );

  readonly draft$ = this.subject.asObservable();

  get snapshot(): BylawsDraft | null {
    return this.subject.value;
  }

  draft(): BylawsDraft | null {
    return this.subject.value;
  }

  currentDraftValue(): BylawsDraft | null {
    return this.subject.value;
  }

  update(partial: Partial<BylawsDraft>) {
    const current = this.subject.value ?? ({} as BylawsDraft);
    const next = { ...current, ...partial } as BylawsDraft;
    this.subject.next(next);
    this.persist(next);
  }

  reset() {
    this.subject.next(null);
    this.removePersisted();
  }

  private persist(value: BylawsDraft) {
    if (!this.storageAvailable()) {
      return;
    }
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(value));
    } catch {
      // Swallow storage quota issues; autosave is best-effort.
    }
  }

  private removePersisted() {
    if (!this.storageAvailable()) {
      return;
    }
    try {
      localStorage.removeItem(this.storageKey);
    } catch {
      // ignore
    }
  }

  private load(): BylawsDraft | null {
    if (!this.storageAvailable()) {
      return null;
    }
    try {
      const raw = localStorage.getItem(this.storageKey);
      return raw ? (JSON.parse(raw) as BylawsDraft) : null;
    } catch {
      return null;
    }
  }

  private storageAvailable(): boolean {
    try {
      return (
        typeof window !== 'undefined' && typeof localStorage !== 'undefined'
      );
    } catch {
      return false;
    }
  }
}
